import React, { useEffect, useRef, useState } from 'react'
import { getCompanies } from '@/api/companies'
import { Company } from '@/types/index'
import { Search, X } from 'lucide-react'

// Bug #334: the Add project modal used to collect the client as free text
// ("Search for company..." placeholder over a plain <input>, going nowhere).
// `lab_project.client_id` is a NOT NULL FK to `lab_company` and `ProjectCreate`
// requires it, so the form has to yield a real company id — a typed name
// cannot be turned into one.
//
// Deliberately server-side search rather than loading every company and
// filtering locally: `GET /companies` already searches name + domain, and the
// list is unbounded. Results render as an inline block under the input rather
// than an absolutely-positioned dropdown, because this lives inside `Modal`
// and a positioned layer would clip against the modal's own scroll container.

const PAGE_SIZE = 8
const DEBOUNCE_MS = 300

interface CompanySelectProps {
  /** Currently selected company id, or undefined when nothing is chosen. */
  value?: string
  /** Display name for `value`, so the field can render without a refetch. */
  valueName?: string
  onChange: (companyId: string | undefined, companyName: string | undefined) => void
  disabled?: boolean
  placeholder?: string
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px 8px 36px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

export const CompanySelect: React.FC<CompanySelectProps> = ({
  value,
  valueName,
  onChange,
  disabled = false,
  placeholder = 'Search for a company...',
}) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Company[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  // Guards against a slow early request landing after a later one and
  // overwriting fresher results.
  const requestSeq = useRef(0)

  useEffect(() => {
    // Once a company is chosen the picker collapses to the selection, so
    // there is nothing to search for.
    if (value) return

    const term = query.trim()
    if (!term) {
      setResults([])
      setHasSearched(false)
      setSearchError(null)
      return
    }

    const seq = ++requestSeq.current
    setIsSearching(true)
    const timer = setTimeout(() => {
      getCompanies({ page: 1, pageSize: PAGE_SIZE, search: term })
        .then((data) => {
          if (seq !== requestSeq.current) return
          setResults(data.items)
          setSearchError(null)
          setHasSearched(true)
        })
        .catch((err: unknown) => {
          if (seq !== requestSeq.current) return
          setResults([])
          setSearchError(err instanceof Error ? err.message : 'Failed to search companies')
        })
        .finally(() => {
          if (seq === requestSeq.current) setIsSearching(false)
        })
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [query, value])

  const clearSelection = () => {
    onChange(undefined, undefined)
    setQuery('')
    setResults([])
    setHasSearched(false)
  }

  if (value) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: '8px 12px',
          border: '1px solid var(--color-light-grey)',
          borderRadius: '6px',
          backgroundColor: 'var(--color-white)',
          fontSize: '14px',
          fontFamily: 'var(--font-family)',
        }}
      >
        <span style={{ fontWeight: 500 }}>{valueName || value}</span>
        <button
          type="button"
          onClick={clearSelection}
          disabled={disabled}
          aria-label="Clear selected company"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '4px',
            backgroundColor: 'var(--color-white)',
            fontSize: '13px',
            fontFamily: 'var(--font-family)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <X size={14} />
          Change
        </button>
      </div>
    )
  }

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <Search
          size={18}
          style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--color-dark-grey)' }}
        />
        <input
          type="text"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
          style={inputStyle}
        />
      </div>

      {searchError && (
        <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#dc2626' }}>{searchError}</p>
      )}

      {isSearching && (
        <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
          Searching...
        </p>
      )}

      {!isSearching && !searchError && hasSearched && results.length === 0 && (
        <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
          No companies match "{query.trim()}".
        </p>
      )}

      {!isSearching && results.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            margin: '6px 0 0 0',
            padding: 0,
            border: '1px solid var(--color-light-grey)',
            borderRadius: '6px',
            maxHeight: '180px',
            overflowY: 'auto',
          }}
        >
          {results.map((company) => (
            <li key={company.id}>
              <button
                type="button"
                onClick={() => onChange(company.id, company.name)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: '1px solid var(--color-light-grey)',
                  backgroundColor: 'var(--color-white)',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontWeight: 500 }}>{company.name}</span>
                {company.domain && (
                  <span style={{ marginLeft: '8px', color: 'var(--color-dark-grey)', fontSize: '13px' }}>
                    {company.domain}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
